/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField,
  MenuItem, Button, CircularProgress, Box, Typography, IconButton,
  Table, TableBody, TableRow, TableCell, Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Swal from 'sweetalert2';
import { api } from '../../../../api';
import CreatableSelect from 'components/CreatableSelect';
import MiniMasterDialog from 'components/MiniMasterDialog';
import { formKeyHandler } from 'utils/formKeyboard';

const TEAL = '#00798E';
const upperSx = { '& .MuiInputBase-input': { textTransform: 'uppercase' } };
const DRUG_SCHEDULES = ['NONE', 'OTC', 'H', 'H1', 'X', 'G', 'J', 'C', 'L'];

const EMPTY = {
  name: '', irm_pharm_categories_id: '', generic_name: '', drug_schedule: 'NONE',
  irm_pharm_manufacturers_id: '', irm_pharm_hsn_codes_id: '', description: '',
  base_unit_id: '', sub_unit_id: '', sub_units_per_base: '',
  is_sub_unit_sellable: 'N', mrp_per_base_unit: '', discount_percentage: '0',
};

// nested-master config keyed by which field triggered "+ Create"
const MINI = {
  category: {
    title: 'New Category',
    fields: [
      { key: 'name', label: 'Category Name *', required: true, upper: true },
      { key: 'description', label: 'Description', upper: true },
    ],
    build: (f) => ({ name: f.name.trim(), description: f.description?.trim() || undefined }),
    create: (p) => api.pharmacy.createCategory(p),
  },
  unit: {
    title: 'New Unit',
    fields: [
      { key: 'name', label: 'Unit Name *', required: true, upper: true },
      { key: 'short_name', label: 'Short Name *', required: true, upper: true },
    ],
    build: (f) => ({ name: f.name.trim(), short_name: f.short_name.trim() }),
    create: (p) => api.pharmacy.createUnit(p),
  },
  manufacturer: {
    title: 'New Manufacturer',
    fields: [
      { key: 'name', label: 'Manufacturer Name *', required: true, upper: true },
      { key: 'short_name', label: 'Short Name', upper: true },
      { key: 'gst_number', label: 'GST Number', upper: true },
    ],
    build: (f) => ({ name: f.name.trim(), short_name: f.short_name?.trim() || undefined, gst_number: f.gst_number?.trim() || undefined }),
    create: (p) => api.pharmacy.createManufacturer(p),
  },
  hsn: {
    title: 'New HSN Code',
    fields: [
      { key: 'hsn_code', label: 'HSN Code *', required: true },
      { key: 'description', label: 'Description *', required: true, upper: true },
      { key: 'gst_percentage', label: 'GST % *', required: true, type: 'number', helperText: 'CGST/SGST auto-split' },
      { key: 'cess_percentage', label: 'Cess %', type: 'number', default: '0' },
    ],
    build: (f) => {
      const g = parseFloat(f.gst_percentage) || 0;
      return {
        hsn_code: f.hsn_code.trim(), description: f.description.trim(),
        gst_percentage: g, cgst_percentage: g / 2, sgst_percentage: g / 2, igst_percentage: g,
        cess_percentage: parseFloat(f.cess_percentage) || 0,
      };
    },
    create: (p) => api.pharmacy.createHsn(p),
  },
};

const newVariantRow = () => ({
  key: `v${Date.now()}${Math.random()}`, variant_name: '', mrp_per_base_unit: '',
  sub_units_per_base: '', is_default: false,
});

/**
 * Full item create from the GRN screen.
 *   props: open, initialName, pharmacy, onClose(), onCreated(fullItem)
 */
export default function ItemQuickAdd({ open, initialName = '', onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [variants, setVariants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [hsnCodes, setHsnCodes] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [saving, setSaving] = useState(false);
  // { kind: 'category'|'unit'|'manufacturer'|'hsn', target: 'base'|'sub'|null, name }
  const [mini, setMini] = useState(null);

  const loadMasters = useCallback(async () => {
    try {
      const [c, u, h, m] = await Promise.all([
        api.pharmacy.listCategories({ is_active: 'Y', limit: 300 }),
        api.pharmacy.listUnits({ is_active: 'Y', limit: 300 }),
        api.pharmacy.listHsn({ is_active: 'Y', limit: 500 }),
        api.pharmacy.listManufacturers({ is_active: 'Y', limit: 300 }),
      ]);
      setCategories(c.data || []); setUnits(u.data || []);
      setHsnCodes(h.data || []); setManufacturers(m.data || []);
    } catch { /* handled */ }
  }, []);

  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY, name: (initialName || '').toUpperCase() });
    setVariants([]);
    setMini(null);
    loadMasters();
  }, [open, initialName, loadMasters]);

  const set = (k, upper) => (e) =>
    setForm((p) => ({ ...p, [k]: upper ? e.target.value.toUpperCase() : e.target.value }));
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const hsnLabel = (h) => `${h.hsn_code} · GST ${h.gst_percentage}%`;

  const openMini = (kind, target = null) => (name) => setMini({ kind, target, name });

  const handleMiniCreated = (rec) => {
    const { kind, target } = mini;
    if (kind === 'category') { setCategories((p) => [rec, ...p]); setF('irm_pharm_categories_id', String(rec.id)); }
    else if (kind === 'manufacturer') { setManufacturers((p) => [rec, ...p]); setF('irm_pharm_manufacturers_id', String(rec.id)); }
    else if (kind === 'hsn') { setHsnCodes((p) => [rec, ...p]); setF('irm_pharm_hsn_codes_id', String(rec.id)); }
    else if (kind === 'unit') {
      setUnits((p) => [rec, ...p]);
      setF(target === 'sub' ? 'sub_unit_id' : 'base_unit_id', String(rec.id));
    }
  };

  const addVariantRow = () => setVariants((p) => [...p, newVariantRow()]);
  const setVariant = (i, patch) => setVariants((p) => p.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeVariant = (i) => setVariants((p) => p.filter((_, idx) => idx !== i));
  const setDefaultVariant = (i) => setVariants((p) => p.map((r, idx) => ({ ...r, is_default: idx === i })));

  const save = async () => {
    if (!form.name.trim()) { Swal.fire({ icon: 'warning', title: 'Item / brand name is required' }); return; }
    if (!form.irm_pharm_categories_id) { Swal.fire({ icon: 'warning', title: 'Category is required' }); return; }
    if (!form.base_unit_id) { Swal.fire({ icon: 'warning', title: 'Base unit is required' }); return; }
    const filledVariants = variants.filter((v) => v.variant_name.trim());
    setSaving(true);
    try {
      const payload = {
        irm_pharm_categories_id: parseInt(form.irm_pharm_categories_id, 10),
        name: form.name.trim(),
        generic_name: form.generic_name.trim() || undefined,
        description: form.description.trim() || undefined,
        drug_schedule: form.drug_schedule || 'NONE',
        irm_pharm_manufacturers_id: form.irm_pharm_manufacturers_id ? parseInt(form.irm_pharm_manufacturers_id, 10) : undefined,
        irm_pharm_hsn_codes_id: form.irm_pharm_hsn_codes_id ? parseInt(form.irm_pharm_hsn_codes_id, 10) : undefined,
        base_unit_id: parseInt(form.base_unit_id, 10),
        sub_unit_id: form.sub_unit_id ? parseInt(form.sub_unit_id, 10) : undefined,
        sub_units_per_base: form.sub_units_per_base ? parseInt(form.sub_units_per_base, 10) : undefined,
        is_sub_unit_sellable: form.is_sub_unit_sellable || 'N',
        mrp_per_base_unit: form.mrp_per_base_unit !== '' ? parseFloat(form.mrp_per_base_unit) : undefined,
        discount_percentage: parseFloat(form.discount_percentage) || 0,
      };
      const res = await api.pharmacy.createItem(payload);
      const newId = (res?.data || res)?.id;
      if (!newId) throw new Error('Item creation failed');

      if (filledVariants.length === 0) {
        await api.pharmacy.addVariant(newId, {
          variant_name: 'STANDARD',
          mrp_per_base_unit: payload.mrp_per_base_unit || undefined,
          sub_units_per_base: payload.sub_units_per_base || undefined,
          is_default: 'Y',
        });
      } else {
        const hasDefault = filledVariants.some((v) => v.is_default);
        for (let i = 0; i < filledVariants.length; i++) {
          const v = filledVariants[i];
          await api.pharmacy.addVariant(newId, {
            variant_name: v.variant_name.trim().toUpperCase(),
            mrp_per_base_unit: v.mrp_per_base_unit !== '' ? parseFloat(v.mrp_per_base_unit) : (payload.mrp_per_base_unit || undefined),
            sub_units_per_base: v.sub_units_per_base ? parseInt(v.sub_units_per_base, 10) : (payload.sub_units_per_base || undefined),
            is_default: (hasDefault ? v.is_default : i === 0) ? 'Y' : 'N',
          });
        }
      }

      const full = await api.pharmacy.getItemById(newId);
      Swal.fire({ icon: 'success', title: 'Item created', toast: true, position: 'top-end', showConfirmButton: false, timer: 1600 });
      onCreated(full?.data || full);
      onClose();
    } catch { /* handled */ }
    finally { setSaving(false); }
  };

  return (
    <>
      <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ color: TEAL, fontWeight: 700, fontSize: '1rem', borderBottom: '1px solid #f0f2f5', pb: 1.5 }}>
          New Material / Item
        </DialogTitle>
        <DialogContent sx={{ pt: '18px !important' }} onKeyDown={formKeyHandler(save)}>
          <Grid container spacing={1.5}>
            {/* ── Core ── */}
            <Grid item xs={12} sm={7}>
              <TextField autoFocus fullWidth size="small" label="Item / Brand Name *" value={form.name} onChange={set('name', true)} sx={upperSx} />
            </Grid>
            <Grid item xs={12} sm={5}>
              <CreatableSelect label="Category *" placeholder="Select / create"
                options={categories.map((c) => ({ value: String(c.id), label: c.name }))}
                value={form.irm_pharm_categories_id}
                onChange={(v) => setF('irm_pharm_categories_id', v)}
                onCreateNew={openMini('category')} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Generic / Salt Name" value={form.generic_name} onChange={set('generic_name', true)} sx={upperSx} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth size="small" label="Drug Schedule" value={form.drug_schedule} onChange={set('drug_schedule')}>
                {DRUG_SCHEDULES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <CreatableSelect label="Manufacturer" placeholder="Select / create"
                options={manufacturers.map((m) => ({ value: String(m.id), label: m.name }))}
                value={form.irm_pharm_manufacturers_id}
                onChange={(v) => setF('irm_pharm_manufacturers_id', v)}
                onCreateNew={openMini('manufacturer')} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <CreatableSelect label="HSN Code" placeholder="Select / create — sets GST"
                options={hsnCodes.map((h) => ({ value: String(h.id), label: hsnLabel(h) }))}
                value={form.irm_pharm_hsn_codes_id}
                onChange={(v) => setF('irm_pharm_hsn_codes_id', v)}
                onCreateNew={openMini('hsn')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Description / Notes" value={form.description} onChange={set('description', true)} sx={upperSx} />
            </Grid>

            {/* ── Units ── */}
            <Grid item xs={12}><Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', mt: 0.5 }}>Units</Typography></Grid>
            <Grid item xs={12} sm={3}>
              <CreatableSelect label="Base Unit *" placeholder="e.g. STRIP"
                options={units.map((u) => ({ value: String(u.id), label: u.short_name || u.name }))}
                value={form.base_unit_id}
                onChange={(v) => setF('base_unit_id', v)}
                onCreateNew={openMini('unit', 'base')} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <CreatableSelect label="Sub Unit (breakable to)" placeholder="e.g. TAB"
                options={units.map((u) => ({ value: String(u.id), label: u.short_name || u.name }))}
                value={form.sub_unit_id}
                onChange={(v) => setF('sub_unit_id', v)}
                onCreateNew={openMini('unit', 'sub')} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="Sub-units per Base" value={form.sub_units_per_base}
                onChange={set('sub_units_per_base')} inputProps={{ min: 1 }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth size="small" label="Sell sub-units?" value={form.is_sub_unit_sellable} onChange={set('is_sub_unit_sellable')}>
                <MenuItem value="N">No — full unit only</MenuItem>
                <MenuItem value="Y">Yes — sell loose</MenuItem>
              </TextField>
            </Grid>

            {/* ── Pricing ── */}
            <Grid item xs={12}><Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', mt: 0.5 }}>Pricing</Typography></Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="MRP / Base Unit" value={form.mrp_per_base_unit}
                onChange={set('mrp_per_base_unit')} inputProps={{ min: 0, step: 0.01 }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="Discount %" value={form.discount_percentage}
                onChange={set('discount_percentage')} inputProps={{ min: 0, max: 100, step: 0.01 }} />
            </Grid>

            {/* ── Variants ── */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Variants <Typography component="span" sx={{ fontSize: '0.62rem', color: '#bbb', textTransform: 'none' }}>(optional — a STANDARD variant is created if left blank)</Typography>
                </Typography>
                <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={addVariantRow}
                  sx={{ textTransform: 'none', color: TEAL, fontSize: '0.72rem', fontWeight: 700 }}>
                  Add variant
                </Button>
              </Box>
              {variants.length > 0 && (
                <Table size="small" sx={{ mt: 0.5, '& td, & th': { border: '1px solid #eee', px: 1, py: 0.4 } }}>
                  <TableBody>
                    <TableRow sx={{ bgcolor: '#fafbfc' }}>
                      <TableCell sx={{ fontSize: '0.66rem', fontWeight: 700 }}>Variant Name</TableCell>
                      <TableCell sx={{ fontSize: '0.66rem', fontWeight: 700, width: 110 }}>MRP</TableCell>
                      <TableCell sx={{ fontSize: '0.66rem', fontWeight: 700, width: 110 }}>Sub/Base</TableCell>
                      <TableCell sx={{ fontSize: '0.66rem', fontWeight: 700, width: 66, textAlign: 'center' }}>Default</TableCell>
                      <TableCell sx={{ width: 34 }} />
                    </TableRow>
                    {variants.map((v, i) => (
                      <TableRow key={v.key}>
                        <TableCell>
                          <TextField variant="standard" fullWidth value={v.variant_name}
                            onChange={(e) => setVariant(i, { variant_name: e.target.value.toUpperCase() })}
                            InputProps={{ disableUnderline: true, sx: { fontSize: '0.75rem' } }} />
                        </TableCell>
                        <TableCell>
                          <TextField variant="standard" type="number" fullWidth value={v.mrp_per_base_unit}
                            onChange={(e) => setVariant(i, { mrp_per_base_unit: e.target.value })}
                            InputProps={{ disableUnderline: true, sx: { fontSize: '0.75rem' } }} />
                        </TableCell>
                        <TableCell>
                          <TextField variant="standard" type="number" fullWidth value={v.sub_units_per_base}
                            onChange={(e) => setVariant(i, { sub_units_per_base: e.target.value })}
                            InputProps={{ disableUnderline: true, sx: { fontSize: '0.75rem' } }} />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Checkbox size="small" checked={!!v.is_default} onChange={() => setDefaultVariant(i)}
                            sx={{ p: 0.25, color: TEAL, '&.Mui-checked': { color: TEAL } }} />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => removeVariant(i)} sx={{ p: 0.25, color: '#d32f2f' }}>
                            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', color: '#7b809a' }}>Cancel</Button>
          <Button onClick={save} variant="contained" disabled={saving}
            sx={{ textTransform: 'none', bgcolor: TEAL, color: 'white !important', fontWeight: 600, minWidth: 150, '&:hover': { bgcolor: '#016476' } }}>
            {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Create Item (Ctrl+↵)'}
          </Button>
        </DialogActions>
      </Dialog>

      {mini && (
        <MiniMasterDialog
          open
          title={MINI[mini.kind].title}
          initialName={mini.name}
          fields={MINI[mini.kind].fields}
          build={MINI[mini.kind].build}
          create={MINI[mini.kind].create}
          onClose={() => setMini(null)}
          onCreated={handleMiniCreated}
        />
      )}
    </>
  );
}
